import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';

function fmtTime(iso: string) { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }

const STATUS_LABEL: Record<string, string> = {
  UPCOMING: 'Upcoming', SUBMISSIONS_OPEN: 'Registration Open', REVIEWING: 'Reviewing',
  ACTIVE: 'Active', COMPLETED: 'Completed', CANCELLED: 'Cancelled',
};

type Tab = 'overview' | 'battles' | 'access-codes' | 'ai' | 'presence' | 'users' | 'announcements' | 'audit' | 'support' | 'health';

export function AdminPage() {
  const { currentUser } = useApp();
  const [tab, setTab] = useState<Tab>('overview');
  const [stats, setStats] = useState<any>(null);
  const [battles, setBattles] = useState<any[]>([]);
  const [aiJobs, setAiJobs] = useState<any[]>([]);
  const [online, setOnline] = useState<any[]>([]);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [health, setHealth] = useState<any>(null);
  const [audit, setAudit] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);

  const loadStats = () => { api.adminPlatformStats().then((d) => setStats(d)).catch((e:any) => window.alert(e.message || 'Request failed')); };
  const loadBattles = () => { api.adminBattles().then((d) => setBattles(d.battles)).catch((e:any) => window.alert(e.message || 'Request failed')); };
  const loadAi = () => { api.adminAiJobs().then((d) => setAiJobs(d.jobs)).catch((e:any) => window.alert(e.message || 'Request failed')); };
  const loadOnline = () => { api.adminOnlineUsers().then((d) => setOnline(d.users)).catch((e:any) => window.alert(e.message || 'Request failed')); };
  const loadAnnouncements = () => { api.adminAnnouncements().then((d) => setAnnouncements(d.announcements)).catch((e:any) => window.alert(e.message || 'Request failed')); };
  const loadHealth = () => { api.adminSystemHealth().then(setHealth).catch((e:any) => window.alert(e.message || 'Request failed')); };
  const loadUsers = () => { api.adminUsers().then((d) => setUsers(d.users)).catch((e:any) => window.alert(e.message || 'Request failed')); };
  const loadAudit = () => { api.adminAudit().then((d) => setAudit(d.audit)).catch((e:any) => window.alert(e.message || 'Request failed')); };

  useEffect(() => {
    loadStats(); loadBattles(); loadAi(); loadOnline(); loadAnnouncements(); loadHealth();
    loadUsers(); loadAudit();
  }, []);

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: 'overview', label: 'Overview', icon: '◧' },
    { id: 'battles', label: 'Battle Management', icon: '⚔' },
    { id: 'access-codes', label: 'Access Codes', icon: '🔑' },
    { id: 'ai', label: 'AI Monitor', icon: '🤖' },
    { id: 'presence', label: 'Online Users', icon: '●' },
    { id: 'users', label: 'User Management', icon: '👥' },
    { id: 'announcements', label: 'Announcements', icon: '📢' },
    { id: 'audit', label: 'Audit Log', icon: '📋' },
    { id: 'support', label: 'Support Inbox', icon: '✉️' },
    { id: 'health', label: 'System Health', icon: '🏥' },
  ];

  return (
    <div className="grid">
      <div className="card">
        <div className="section-title">Admin Control Room</div>
        <div className="muted" style={{ fontSize: 13 }}>
          Logged in as <strong>{currentUser?.username}</strong> (ADMIN). You see the same platform as consumers,
          with admin controls layered on top. All admin actions are logged to the audit trail.
        </div>
      </div>

      {/* Tab navigation */}
      <div className="card" style={{ padding: '4px 8px' }}>
        <div className="flex gap-4 wrap">
          {tabs.map((t) => (
            <button
              key={t.id}
              className={`btn btn-sm ${tab === t.id ? 'btn-gold' : 'btn-ghost'}`}
              onClick={() => setTab(t.id)}
              style={{ fontSize: 13 }}
            >
              <span style={{ marginRight: 4 }}>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && stats && (
        <div className="card">
          <div className="section-title">Platform Overview</div>
          <div className="grid cols-3">
            <div className="stat">
              <div className="stat-label">Total Users</div>
              <div className="stat-value">{stats.totalUsers || 0}</div>
              <div className="muted" style={{ fontSize: 12 }}>{stats.newToday || 0} new today</div>
            </div>
            <div className="stat">
              <div className="stat-label">Total Songs</div>
              <div className="stat-value">{stats.totalSongs || 0}</div>
              <div className="muted" style={{ fontSize: 12 }}>{stats.pendingSongs || 0} under review</div>
            </div>
            <div className="stat">
              <div className="stat-label">Active Battles</div>
              <div className="stat-value">{stats.activeBattles || 0}</div>
              <div className="muted" style={{ fontSize: 12 }}>{stats.totalBattles || 0} total</div>
            </div>
          </div>
          <div className="divider" />
          <div className="section-title" style={{ fontSize: 15 }}>AI Generation Usage</div>
          <div className="grid cols-4">
            <div className="stat">
              <div className="stat-label">Total Jobs</div>
              <div className="stat-value">{stats.totalAiJobs || 0}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Running</div>
              <div className="stat-value">{stats.aiJobsRunning || 0}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Failed</div>
              <div className="stat-value" style={{ color: 'var(--iam-danger)' }}>{stats.aiJobsFailed || 0}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Online Now</div>
              <div className="stat-value">{stats.onlineCount || 0}</div>
            </div>
          </div>
          <div className="divider" />
          <div className="section-title" style={{ fontSize: 15 }}>User Roles</div>
          <div className="grid cols-3">
            <div className="stat">
              <div className="stat-label">Admins</div>
              <div className="stat-value">{stats.roles?.ADMIN || 0}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Judges</div>
              <div className="stat-value">{stats.roles?.JUDGE || 0}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Artists</div>
              <div className="stat-value">{stats.roles?.USER || 0}</div>
            </div>
          </div>
        </div>
      )}

      {/* BATTLE MANAGEMENT */}
      {tab === 'battles' && (
        <div className="card">
          <div className="section-title">Battle Management</div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Create battles in the Arena page (Create Battle button). Here you can manage status, assign judges, and finalize.
          </div>
          <table className="table">
            <thead>
              <tr><th>Title</th><th>Genre</th><th>Status</th><th>Entrants</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {battles.map((b) => {
                const entrantCount = b.entryCount || 0;
                return (
                  <tr key={b.id}>
                    <td>{b.title}</td>
                    <td>{b.genre}</td>
                    <td>
                      <select
                        className="select"
                        style={{ fontSize: 12, padding: '2px 6px' }}
                        value={b.status}
                        onChange={(e) => {
                          api.updateBattle(b.id, { status: e.target.value }).then(loadBattles).catch((e:any) => window.alert(e.message || 'Request failed'));
                        }}
                      >
                        {Object.entries(STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </td>
                    <td>{entrantCount}/{b.maximumEntrants}</td>
                    <td>
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => {
                          const judgeIds = prompt('Enter judge user IDs (comma-separated):');
                          if (judgeIds) {
                            api.assignJudges(b.id, judgeIds.split(',').map(s => s.trim())).then(loadBattles).catch((e:any) => window.alert(e.message || 'Request failed'));
                          }
                        }}
                      >
                        Assign Judges
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* AI MONITOR */}
      {tab === 'access-codes' && (
        <AccessCodesTab />
      )}

      {tab === 'ai' && (
        <div className="card">
          <div className="section-title">AI Generation Monitor</div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Track all AI music generation jobs across the platform.
          </div>
          {aiJobs.length === 0 ? (
            <div className="muted" style={{ fontSize: 14 }}>No AI jobs yet.</div>
          ) : (
            <table className="table">
              <thead>
                <tr><th>User</th><th>Type</th><th>Status</th><th>Prompt</th><th>Created</th></tr>
              </thead>
              <tbody>
                {aiJobs.slice(0, 20).map((j) => (
                  <tr key={j.id}>
                    <td>{j.userId}</td>
                    <td>{j.kind}</td>
                    <td>
                      <span className={`chip chip-${j.status === 'completed' ? 'approved' : j.status === 'error' ? 'denied' : 'pending'}`}>
                        {j.status}
                      </span>
                    </td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.prompt}</td>
                    <td style={{ fontSize: 12 }}>{fmtTime(j.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ONLINE USERS */}
      {tab === 'presence' && (
        <div className="card">
          <div className="section-title">Online Users</div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Users who have been active in the last 5 minutes. This is separate from LiveKit battle participation.
          </div>
          {online.length === 0 ? (
            <div className="muted" style={{ fontSize: 14 }}>No users online right now.</div>
          ) : (
            <table className="table">
              <thead><tr><th>User</th><th>Role</th><th>Last Seen</th></tr></thead>
              <tbody>
                {online.map((u) => (
                  <tr key={u.userId}>
                    <td>{u.username}</td>
                    <td>{u.role}</td>
                    <td style={{ fontSize: 12 }}>{fmtTime(u.lastActive || u.lastSeen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* USER MANAGEMENT */}
      {tab === 'users' && (
        <div className="card">
          <div className="section-title">User Management</div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            View users, verify identity tiers, and manage account status. Suspended users cannot log in.
          </div>
          <table className="table">
            <thead><tr><th>Username</th><th>Email</th><th>Role</th><th>Tier</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {(users || []).map((u: any) => (
                <tr key={u.id}>
                  <td>{u.username}</td>
                  <td style={{ fontSize: 12 }}>{u.email}</td>
                  <td><span className="chip">{u.role}</span></td>
                  <td>{u.tier}</td>
                  <td>
                    <span className={`chip chip-${u.status === 'active' ? 'approved' : 'denied'}`}>
                      {u.status}
                    </span>
                  </td>
                  <td>
                    {u.role==='ADMIN' ? <span className="muted">Admin protected</span> : u.status === 'active' ? (
                      <button
                        className="btn btn-outline btn-sm"
                        style={{ fontSize: 12, color: 'var(--iam-danger)' }}
                        onClick={() => {
                          if (confirm(`Suspend ${u.username}? They will be logged out immediately.`)) {
                            api.suspendUser(u.id, 'Admin suspension', '7d').then(() => {
                              setUsers(users.map((uu: any) => uu.id === u.id ? { ...uu, status: 'suspended' } : uu));
                            }).catch((e:any) => window.alert(e.message || 'Request failed'));
                          }
                        }}
                      >
                        Suspend
                      </button>
                    ) : (
                      <button
                        className="btn btn-outline btn-sm"
                        style={{ fontSize: 12 }}
                        onClick={() => {
                          api.reactivateUser(u.id).then(() => {
                            setUsers(users.map((uu: any) => uu.id === u.id ? { ...uu, status: 'active' } : uu));
                          }).catch((e:any) => window.alert(e.message || 'Request failed'));
                        }}
                      >
                        Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ANNOUNCEMENTS */}
      {tab === 'announcements' && (
        <AnnouncementsTab
          announcements={announcements}
          onRefresh={loadAnnouncements}
        />
      )}

      {/* AUDIT LOG */}
      {tab === 'audit' && (
        <div className="card">
          <div className="section-title">Audit Trail</div>
          <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
            Every admin action is recorded here. This trail cannot be deleted.
          </div>
          {audit.length === 0 ? (
            <div className="muted" style={{ fontSize: 14 }}>No admin actions logged yet.</div>
          ) : (
            <table className="table">
              <thead><tr><th>Action</th><th>Target</th><th>Actor</th><th>Time</th><th>Details</th></tr></thead>
              <tbody>
                {audit.slice(0, 50).map((a, i) => (
                  <tr key={i}>
                    <td><span className="chip chip-gold">{a.action}</span></td>
                    <td style={{ fontSize: 12 }}>{a.targetId || '—'}</td>
                    <td style={{ fontSize: 12 }}>{a.actorId}</td>
                    <td style={{ fontSize: 12 }}>{fmtTime(a.timestamp)}</td>
                    <td style={{ fontSize: 12 }}>{JSON.stringify(a.details || {}).slice(0, 80)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* SUPPORT INBOX */}
      {tab === 'support' && <SupportTab />}

      {/* SYSTEM HEALTH */}
      {tab === 'health' && health && (
        <div className="card">
          <div className="section-title">System Health</div>
          <div className="grid cols-3">
            <div className="stat">
              <div className="stat-label">Payment Mode</div>
              <div className="stat-value" style={{ fontSize: 14 }}>{health.payments}</div>
            </div>
            <div className="stat">
              <div className="stat-label">AI ElevenLabs Key</div>
              <div className="stat-value" style={{ fontSize: 14, color: health.ai?.elevenlabs ? 'var(--iam-success)' : 'var(--iam-danger)' }}>
                {health.ai?.elevenlabs ? 'Configured' : 'Missing'}
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">LiveKit Server</div>
              <div className="stat-value" style={{ fontSize: 14, color: health.live?.configured ? 'var(--iam-success)' : 'var(--iam-gold)' }}>
                {health.live?.configured ? 'Configured' : 'Not configured'}
              </div>
            </div>
          </div>
          <div className="divider" />
          <div className="section-title" style={{ fontSize: 15 }}>Data Counts</div>
          <table className="table">
            <thead><tr><th>Collection</th><th>Records</th></tr></thead>
            <tbody>
              {Object.entries(health.data || {}).map(([k, v]) => (
                <tr key={k}><td>{k}</td><td>{String(v)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AnnouncementsTab({ announcements, onRefresh }: { announcements: any[]; onRefresh: () => void }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('all');
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!title.trim() || !body.trim()) { setError('Title and body are required.'); return; }
    setCreating(true); setError(null);
    try {
      await api.createAnnouncement({ title: title.trim(), body: body.trim(), audience });
      setTitle(''); setBody(''); setAudience('all');
      onRefresh();
    } catch (e: any) {
      setError(e?.detail?.message || e?.message || 'Failed to create announcement');
    } finally { setCreating(false); }
  };

  return (
    <div className="card">
      <div className="section-title">Announcements</div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
        Push notifications to specific user tiers or all users. Announcements appear in the notifications feed.
      </div>

      <div className="form-group">
        <label className="label">Title</label>
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. New battle season starts Monday" />
      </div>
      <div className="form-group">
        <label className="label">Body</label>
        <textarea className="textarea" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Announcement details..." />
      </div>
      <div className="form-group">
        <label className="label">Audience</label>
        <select className="select" value={audience} onChange={(e) => setAudience(e.target.value)}>
          <option value="all">All Users</option>
          <option value="FREE">Free Tier</option>
          <option value="TIER_1">Contract 1</option>
          <option value="TIER_2">Contract 2</option>
          <option value="TIER_3">Contract 3</option>
          <option value="TIER_4">Contract 4</option>
        </select>
      </div>
      <button className="btn btn-gold" onClick={handleCreate} disabled={creating}>
        {creating ? 'Publishing...' : 'Publish Announcement'}
      </button>
      {error && <div className="field-hint" style={{ color: 'var(--iam-danger)', marginTop: 8 }}>{error}</div>}

      <div className="divider" />
      <div className="section-title" style={{ fontSize: 15 }}>Published Announcements</div>
      {announcements.length === 0 ? (
        <div className="muted" style={{ fontSize: 14 }}>No announcements yet.</div>
      ) : (
        <table className="table">
          <thead><tr><th>Title</th><th>Audience</th><th>Author</th><th>Date</th></tr></thead>
          <tbody>
            {announcements.map((a) => (
              <tr key={a.id}>
                <td>{a.title}</td>
                <td>{a.audience}</td>
                <td style={{ fontSize: 12 }}>{a.authorUsername}</td>
                <td style={{ fontSize: 12 }}>{fmtTime(a.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---- Monthly Access Codes tab ----
function AccessCodesTab() {
  const [data, setData] = useState<{ cycle: string; codes: any[]; verifications: any[]; cycles: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = (cycle?: string) => {
    api.adminAccessCodes(cycle).then(setData).catch((e) => setErr(e.message || 'Could not load codes'));
  };
  useEffect(() => { load(); }, []);

  const generate = () => {
    setBusy(true); setErr(null);
    api.generateAccessCodes(data?.cycle)
      .then((d) => { load(d.cycle); })
      .catch((e) => setErr(e.message || 'Could not generate codes'))
      .finally(() => setBusy(false));
  };

  const copy = (code: string) => {
    navigator.clipboard?.writeText(code).then(() => { setCopied(code); setTimeout(() => setCopied(null), 1500); }).catch((e:any) => window.alert(e.message || 'Request failed'));
  };

  if (!data) return <div className="card"><div className="section-title">Monthly Access Codes</div><div className="muted">Loading…</div></div>;

  return (
    <div className="card">
      <div className="section-title">Monthly Access Codes</div>
      <div className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
        Rotating codes gate the live finale. The platform-wide <strong>judge code</strong> unlocks scoring for the month.
        Each <strong>genre finalist code</strong> unlocks that genre's live feed. Codes rotate monthly and never reuse.
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <button className={`btn ${busy ? 'btn-ghost' : 'btn-gold'}`} onClick={generate} disabled={busy}>
          {busy ? 'Generating…' : 'Generate monthly codes'}
        </button>
        <select
          className="input"
          style={{ width: 'auto', minWidth: 130 }}
          value={data.cycle}
          onChange={(e) => load(e.target.value)}
        >
          {(data.cycles.length ? data.cycles : [data.cycle]).map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <span className="muted" style={{ fontSize: 12 }}>Current cycle: {data.cycle}</span>
      </div>

      {err && <div className="alert alert-error" style={{ marginBottom: 12 }}>{err}</div>}

      {data.codes.length === 0 ? (
        <div className="muted">No codes for this cycle yet. Click “Generate monthly codes” to create the judge + per-genre finalist codes.</div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Role</th><th>Genre</th><th>Code</th><th>Created</th><th></th>
            </tr>
          </thead>
          <tbody>
            {data.codes.map((c) => (
              <tr key={c.id}>
                <td>
                  <span className={`badge ${c.kind === 'JUDGE' ? 'badge-gold' : 'badge-blue'}`} style={{ fontSize: 10 }}>
                    {c.kind === 'JUDGE' ? 'Judge' : 'Finalist'}
                  </span>
                </td>
                <td style={{ fontSize: 13 }}>{c.genre || '— platform-wide —'}</td>
                <td>
                  <code style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, color: 'var(--gold-bright, #f2d36c)' }}>{c.code}</code>
                </td>
                <td style={{ fontSize: 12 }}>{new Date(c.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                <td>
                  <button className="btn btn-sm btn-ghost" onClick={() => copy(c.code)}>
                    {copied === c.code ? '✓ Copied' : 'Copy'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data.verifications.length > 0 && (
        <>
          <div className="section-title" style={{ fontSize: 14, marginTop: 20 }}>Verified this cycle</div>
          <table className="table">
            <thead><tr><th>User</th><th>Access</th><th>Genre</th><th>Verified</th></tr></thead>
            <tbody>
              {data.verifications.map((v) => (
                <tr key={v.id}>
                  <td style={{ fontSize: 13 }}>{v.username}</td>
                  <td><span className={`badge ${v.kind === 'JUDGE' ? 'badge-gold' : 'badge-blue'}`} style={{ fontSize: 10 }}>{v.kind === 'JUDGE' ? 'Judge' : 'Finalist'}</span></td>
                  <td style={{ fontSize: 13 }}>{v.genre || '—'}</td>
                  <td style={{ fontSize: 12 }}>{new Date(v.verifiedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function SupportTab() {
  const [tickets, setTickets] = useState<any[]>([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.adminSupport().then((d) => { setTickets(d.tickets || []); setOpenCount(d.openCount || 0); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const update = async (id: string, status: string) => {
    setBusy(true);
    try { await api.updateSupportTicket(id, { status }); load(); } catch {} finally { setBusy(false); }
  };
  const sendReply = async (id: string) => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await api.updateSupportTicket(id, { status: 'RESOLVED', reply }); setReply(''); setReplyTo(null); load(); } catch {} finally { setBusy(false); }
  };

  return (
    <>
      <div className="card">
        <div className="section-title">Support Inbox</div>
        <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
          Messages from the public Contact Support form. Reply right here — the ticket closes when you send a reply.
        </div>
        <span className="chip chip-gold">{openCount} open</span>
      </div>
      {loading ? (
        <div className="card muted">Loading…</div>
      ) : tickets.length === 0 ? (
        <div className="card muted">No support messages yet.</div>
      ) : (
        tickets.map((t) => (
          <div key={t.id} className="card">
            <div className="flex gap-8 wrap" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>{t.subject}</strong>
              <span className="chip" style={{ fontSize: 11 }}>{t.status}</span>
            </div>
            <div className="muted" style={{ fontSize: 12, margin: '4px 0 8px' }}>{t.email} {t.username ? `· @${t.username}` : ''} · {fmtTime(t.createdAt)}</div>
            <p style={{ fontSize: 14, margin: 0 }}>{t.message}</p>
            {t.reply && <div className="chip chip-approved" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>Reply sent: {t.reply}</div>}
            {t.status === 'OPEN' && (
              replyTo === t.id ? (
                <div className="grid" style={{ gap: 8, marginTop: 10 }}>
                  <textarea className="textarea" value={reply} rows={3} placeholder="Write a reply…" onChange={(e) => setReply(e.target.value)} />
                  <div className="flex gap-8">
                    <button className="btn btn-primary btn-sm" onClick={() => sendReply(t.id)} disabled={busy || !reply.trim()}>{busy ? 'Sending…' : 'Send & close'}</button>
                    <button className="btn btn-outline btn-sm" onClick={() => { setReplyTo(null); setReply(''); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-8" style={{ marginTop: 10 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => setReplyTo(t.id)}>Reply</button>
                  <button className="btn btn-outline btn-sm" onClick={() => update(t.id, 'CLOSED')} disabled={busy}>Close without reply</button>
                </div>
              )
            )}
          </div>
        ))
      )}
    </>
  );
}
